import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
    ShieldExclamationIcon,
    ArrowPathIcon,
    ClockIcon,
    PlusIcon,
    CheckCircleIcon,
    XCircleIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    KeyIcon,
    AtSymbolIcon,
} from '@heroicons/react/24/outline';
import moment from 'moment';

// Import services
import Fail2banService from '../../services/fail2ban.service';

// Import components
import Spinner from '../../components/ui/spinner.component';

export default function AdminFail2banView() {
    const [bans, setBans] = useState([]);
    const [stats, setStats] = useState(null);
    const [whitelist, setWhitelist] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [showAddWhitelist, setShowAddWhitelist] = useState(false);
    const [newWhitelistIp, setNewWhitelistIp] = useState('');
    const [newWhitelistReason, setNewWhitelistReason] = useState('');
    const [newWhitelistHours, setNewWhitelistHours] = useState('');
    const [addingWhitelist, setAddingWhitelist] = useState(false);
    const [expandedRows, setExpandedRows] = useState(new Set());

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [bansResponse, statsResponse, whitelistResponse] = await Promise.all([
                Fail2banService.getBans(),
                Fail2banService.getStats(),
                Fail2banService.getWhitelist()
            ]);
            setBans(bansResponse.data.bans);
            setStats(statsResponse.data);
            setWhitelist(whitelistResponse.data.whitelist);
        } catch (err) {
            console.error(err);
            toast.error("Er is iets fout gegaan bij het laden van de data.");
        } finally {
            setLoading(false);
        }
    };

    const refreshData = async () => {
        try {
            setRefreshing(true);
            const [bansResponse, statsResponse, whitelistResponse] = await Promise.all([
                Fail2banService.getBans(),
                Fail2banService.getStats(),
                Fail2banService.getWhitelist()
            ]);
            setBans(bansResponse.data.bans);
            setStats(statsResponse.data);
            setWhitelist(whitelistResponse.data.whitelist);
        } catch (err) {
            console.error(err);
            toast.error("Er is iets fout gegaan bij het vernieuwen.");
        } finally {
            setRefreshing(false);
        }
    };

    const unbanIP = async (ip, endpoint = null) => {
        try {
            await Fail2banService.unbanIP(ip, endpoint);
            toast.success(`IP ${ip} is gedeblokkeerd`);
            await refreshData();
        } catch (err) {
            console.error(err);
            toast.error("Er is iets fout gegaan bij het deblokkeren.");
        }
    };

    const getEndpointLabel = (endpoint) => {
        switch (endpoint) {
            case 'pin_login':
                return 'PIN Login';
            case 'email_login':
                return 'Email Login';
            default:
                return endpoint;
        }
    };

    const getEndpointColor = (endpoint) => {
        switch (endpoint) {
            case 'pin_login':
                return 'bg-red-100 text-red-800';
            case 'email_login':
                return 'bg-blue-100 text-blue-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const addToWhitelist = async () => {
        if (!newWhitelistIp) {
            toast.error("Voer een IP-adres in");
            return;
        }

        try {
            setAddingWhitelist(true);
            const expiresIn = newWhitelistHours ? parseInt(newWhitelistHours) : null;
            await Fail2banService.addToWhitelist(newWhitelistIp, newWhitelistReason, expiresIn);
            toast.success(`IP ${newWhitelistIp} toegevoegd aan whitelist`);

            // Reset form
            setNewWhitelistIp('');
            setNewWhitelistReason('');
            setNewWhitelistHours('');
            setShowAddWhitelist(false);

            await refreshData();
        } catch (err) {
            console.error(err);
            toast.error("Er is iets fout gegaan bij het toevoegen aan de whitelist.");
        } finally {
            setAddingWhitelist(false);
        }
    };

    const removeFromWhitelist = async (ipAddress) => {
        try {
            await Fail2banService.removeFromWhitelist(ipAddress);
            toast.success(`IP ${ipAddress} verwijderd van whitelist`);
            await refreshData();
        } catch (err) {
            console.error(err);
            toast.error("Er is iets fout gegaan bij het verwijderen van de whitelist.");
        }
    };

    const toggleRowExpansion = (banKey) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(banKey)) {
            newExpanded.delete(banKey);
        } else {
            newExpanded.add(banKey);
        }
        setExpandedRows(newExpanded);
    };


    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Spinner />
            </div>
        );
    }

    return (
        <main className="flex-1">
            <div className="py-6">
                <div className="mx-auto px-4 sm:px-6 md:px-8">
                    <div className="flex justify-between items-center">
                        <div>
                            <h1 className="text-2xl font-semibold text-gray-900">Beveiligingsbeheer</h1>
                            <p className="mt-1 text-sm text-gray-600">
                                Overzicht van geblokkeerde IP-adressen en verdachte inlogpogingen
                            </p>
                        </div>
                        <button
                            onClick={refreshData}
                            disabled={refreshing}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                            <ArrowPathIcon className={`-ml-1 mr-2 h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
                            Vernieuwen
                        </button>
                    </div>

                    {/* Statistics Cards */}
                    {stats && (
                        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                            <div className="bg-white overflow-hidden shadow rounded-lg">
                                <div className="p-5">
                                    <div className="flex items-center">
                                        <div className="flex-shrink-0">
                                            <ShieldExclamationIcon className="h-5 w-5 text-gray-500" />
                                        </div>
                                        <div className="ml-5 w-0 flex-1">
                                            <dl>
                                                <dt className="text-sm font-medium text-gray-500 truncate">
                                                    Geblokkeerde IPs
                                                </dt>
                                                <dd className="flex items-baseline">
                                                    <div className="text-lg font-semibold text-gray-900">
                                                        {stats.active_bans}
                                                    </div>
                                                </dd>
                                                <dd className="text-xs text-gray-400 mt-1">
                                                    Momenteel actief geblokkeerd
                                                </dd>
                                            </dl>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white overflow-hidden shadow rounded-lg">
                                <div className="p-5">
                                    <div className="flex items-center">
                                        <div className="flex-shrink-0">
                                            <KeyIcon className="h-5 w-5 text-gray-500" />
                                        </div>
                                        <div className="ml-5 w-0 flex-1">
                                            <dl>
                                                <dt className="text-sm font-medium text-gray-500 truncate">
                                                    PIN blokkades
                                                </dt>
                                                <dd className="flex items-baseline">
                                                    <div className="text-lg font-semibold text-gray-900">
                                                        {stats.endpoint_stats?.pin_login || 0}
                                                    </div>
                                                </dd>
                                                <dd className="text-xs text-gray-400 mt-1">
                                                    Geblokkeerd na 3 pogingen
                                                </dd>
                                            </dl>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white overflow-hidden shadow rounded-lg">
                                <div className="p-5">
                                    <div className="flex items-center">
                                        <div className="flex-shrink-0">
                                            <AtSymbolIcon className="h-5 w-5 text-gray-500" />
                                        </div>
                                        <div className="ml-5 w-0 flex-1">
                                            <dl>
                                                <dt className="text-sm font-medium text-gray-500 truncate">
                                                    Email blokkades
                                                </dt>
                                                <dd className="flex items-baseline">
                                                    <div className="text-lg font-semibold text-gray-900">
                                                        {stats.endpoint_stats?.email_login || 0}
                                                    </div>
                                                </dd>
                                                <dd className="text-xs text-gray-400 mt-1">
                                                    Geblokkeerd na 5 pogingen
                                                </dd>
                                            </dl>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Active Bans Table */}
                    <div className="mt-8">
                        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                            <div className="px-4 py-5 sm:px-6">
                                <h3 className="text-lg leading-6 font-medium text-gray-900">
                                    Geblokkeerde IP-adressen
                                </h3>
                                <p className="mt-1 text-sm text-gray-600">
                                    IPs die te veel mislukte inlogpogingen hebben gedaan
                                </p>
                            </div>
                            {bans.length > 0 ? (
                                <div className="border-t border-gray-200">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">

                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    IP adres
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Endpoint
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Geblokkeerd op
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Verloopt over
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Pogingen
                                                </th>
                                                <th className="relative px-6 py-3">
                                                    <span className="sr-only">Acties</span>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {bans.map((ban, index) => {
                                                const banKey = `${ban.ip_address}-${ban.endpoint}-${index}`;
                                                const isExpanded = expandedRows.has(banKey);
                                                const hasAttemptedValues = ban.attempted_values && ban.attempted_values.length > 0;

                                                return (
                                                    <React.Fragment key={banKey}>
                                                        <tr>
                                                            <td className="px-2 py-4 whitespace-nowrap">
                                                                {hasAttemptedValues && (
                                                                    <button
                                                                        onClick={() => toggleRowExpansion(banKey)}
                                                                        className="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                                                    >
                                                                        {isExpanded ? (
                                                                            <ChevronDownIcon className="h-4 w-4" />
                                                                        ) : (
                                                                            <ChevronRightIcon className="h-4 w-4" />
                                                                        )}
                                                                    </button>
                                                                )}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <div className="text-sm font-medium text-gray-900">
                                                                    {ban.ip_address}
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getEndpointColor(ban.endpoint)}`}>
                                                                    {getEndpointLabel(ban.endpoint)}
                                                                </span>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {moment(ban.banned_at).format('DD-MM-YYYY HH:mm')}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <div className="flex items-center text-sm text-gray-500">
                                                                    <ClockIcon className="h-4 w-4 mr-1" />
                                                                    {ban.remaining_minutes} minuten
                                                                </div>
                                                                <div className="text-xs text-gray-400">
                                                                    ({moment(ban.expires_at).format('HH:mm')})
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {ban.attempt_count}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                                <button
                                                                    onClick={() => unbanIP(ban.ip_address, ban.endpoint)}
                                                                    className="text-indigo-600 hover:text-indigo-900"
                                                                >
                                                                    Deblokkeer
                                                                </button>
                                                            </td>
                                                        </tr>
                                                        {isExpanded && hasAttemptedValues && (
                                                            <tr className="bg-gray-50">
                                                                <td colSpan="8" className="px-6 py-4">
                                                                    <div className="text-sm">
                                                                        <h4 className="font-semibold mb-2 text-gray-700">Geprobeerde waarden:</h4>
                                                                        <div className="space-y-1">
                                                                            {ban.attempted_values.map((av, idx) => (
                                                                                <div key={idx} className="flex items-center space-x-4 text-gray-600">
                                                                                    <span className="text-xs text-gray-400">
                                                                                        {moment(av.timestamp).format('HH:mm:ss')}
                                                                                    </span>
                                                                                    <span className="font-mono bg-white px-2 py-1 rounded border border-gray-200">
                                                                                        {ban.endpoint === 'pin_login' ?
                                                                                            `PIN: ${av.value}` :
                                                                                            av.value
                                                                                        }
                                                                                    </span>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center py-12">
                                    <ShieldExclamationIcon className="mx-auto h-12 w-12 text-gray-400" />
                                    <h3 className="mt-2 text-sm font-medium text-gray-900">
                                        Geen blokkades
                                    </h3>
                                    <p className="mt-1 text-sm text-gray-500">
                                        Er zijn momenteel geen verdachte IP-adressen geblokkeerd.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>


                    {/* Whitelist Table */}
                    <div className="mt-8">
                        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                            <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
                                <h3 className="text-lg leading-6 font-medium text-gray-900">
                                    IP Whitelist
                                </h3>
                                <button
                                    onClick={() => setShowAddWhitelist(true)}
                                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                >
                                    <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
                                    Toevoegen
                                </button>
                            </div>
                            {whitelist.length > 0 ? (
                                <div className="border-t border-gray-200">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    IP adres
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Reden
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Toegevoegd door
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Toegevoegd op
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Type
                                                </th>
                                                <th className="relative px-6 py-3">
                                                    <span className="sr-only">Acties</span>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {whitelist.map((entry) => (
                                                <tr key={entry.ip_address}>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {entry.ip_address}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="text-sm text-gray-500">
                                                            {entry.reason || '-'}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-gray-500">
                                                            {entry.added_by.startsWith('auto-admin:')
                                                                ? entry.added_by.replace('auto-admin:', '') + ' (auto)'
                                                                : entry.added_by}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        {moment(entry.added_at).format('DD-MM-YYYY HH:mm')}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        {entry.permanent ? (
                                                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                                                <CheckCircleIcon className="h-4 w-4 mr-1" />
                                                                Permanent
                                                            </span>
                                                        ) : (
                                                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                                                <ClockIcon className="h-4 w-4 mr-1" />
                                                                Tijdelijk ({entry.remaining_hours}u)
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                        <button
                                                            onClick={() => removeFromWhitelist(entry.ip_address)}
                                                            className="text-red-600 hover:text-red-900"
                                                        >
                                                            Verwijderen
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center py-12">
                                    <CheckCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
                                    <h3 className="mt-2 text-sm font-medium text-gray-900">
                                        Geen whitelist items
                                    </h3>
                                    <p className="mt-1 text-sm text-gray-500">
                                        Er zijn momenteel geen IP-adressen op de whitelist.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Add Whitelist Modal */}
                    {showAddWhitelist && (
                        <div className="fixed z-10 inset-0 overflow-y-auto">
                            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                                <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                                    <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                                </div>
                                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                                <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                                    <div>
                                        <div className="mt-3 text-center sm:mt-0 sm:text-left">
                                            <h3 className="text-lg leading-6 font-medium text-gray-900">
                                                IP toevoegen aan whitelist
                                            </h3>
                                            <div className="mt-4">
                                                <label htmlFor="ip" className="block text-sm font-medium text-gray-700">
                                                    IP-adres
                                                </label>
                                                <input
                                                    type="text"
                                                    name="ip"
                                                    id="ip"
                                                    value={newWhitelistIp}
                                                    onChange={(e) => setNewWhitelistIp(e.target.value)}
                                                    className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                                                    placeholder="192.168.1.1"
                                                />
                                            </div>
                                            <div className="mt-4">
                                                <label htmlFor="reason" className="block text-sm font-medium text-gray-700">
                                                    Reden (optioneel)
                                                </label>
                                                <input
                                                    type="text"
                                                    name="reason"
                                                    id="reason"
                                                    value={newWhitelistReason}
                                                    onChange={(e) => setNewWhitelistReason(e.target.value)}
                                                    className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                                                    placeholder="Kantoor IP"
                                                />
                                            </div>
                                            <div className="mt-4">
                                                <label htmlFor="hours" className="block text-sm font-medium text-gray-700">
                                                    Verloopt over (uren) - laat leeg voor permanent
                                                </label>
                                                <input
                                                    type="number"
                                                    name="hours"
                                                    id="hours"
                                                    value={newWhitelistHours}
                                                    onChange={(e) => setNewWhitelistHours(e.target.value)}
                                                    className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                                                    placeholder="24"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                                        <button
                                            type="button"
                                            disabled={addingWhitelist}
                                            onClick={addToWhitelist}
                                            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:col-start-2 sm:text-sm disabled:opacity-50"
                                        >
                                            {addingWhitelist ? 'Toevoegen...' : 'Toevoegen'}
                                        </button>
                                        <button
                                            type="button"
                                            disabled={addingWhitelist}
                                            onClick={() => {
                                                setShowAddWhitelist(false);
                                                setNewWhitelistIp('');
                                                setNewWhitelistReason('');
                                                setNewWhitelistHours('');
                                            }}
                                            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                                        >
                                            Annuleren
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </main>
    );
}